import React, { useMemo } from 'react';
import { Box, Chip, Typography, alpha } from '@mui/material';
import {
  buildClaimTitleFromLineItems,
  formatClaimLineItemAmount,
  sumClaimLineItemAmounts,
} from '../../../utils/claimLineItems';

const TEXT_BODY = '#E2E8F0';
const TEXT_MUTED = '#64748B';
const SURFACE_TOP = '#152640';

/**
 * Hierarchical purchased-items panel: summary parent row + numbered child list.
 * Matches detail-page section styling (Budget / Borrow / Claim details).
 */
const ClaimLineItemsHierarchy = ({
  items = [],
  accent = '#38BDF8',
  accent2 = '#818CF8',
  maxHeight = 280,
  dense = false,
  showSummary = true,
  summaryOnly = false,
}) => {
  const filled = useMemo(
    () => (items || []).filter((row) => String(row?.name || '').trim()),
    [items],
  );

  if (!filled.length) return null;

  const primary = filled[0].name;
  const extraCount = Math.max(0, filled.length - 1);
  const lineTotal = sumClaimLineItemAmounts(filled);
  const summaryTitle = buildClaimTitleFromLineItems(filled);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: dense ? 0.85 : 1.1 }}>
      {showSummary && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          px: 1.25,
          py: dense ? 0.85 : 1,
          borderRadius: '9px',
          background: alpha(SURFACE_TOP, 0.45),
          border: `1px solid ${alpha(accent, 0.14)}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
            <Typography sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: dense ? '0.8rem' : '0.84rem',
              fontWeight: 600,
              color: TEXT_BODY,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {primary}
            </Typography>
            {extraCount > 0 && (
              <Chip
                label={`+${extraCount}`}
                size="small"
                sx={{
                  height: 22,
                  flexShrink: 0,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  bgcolor: alpha(accent, 0.12),
                  color: accent,
                  border: `1px solid ${alpha(accent, 0.28)}`,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography sx={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.68rem',
              color: alpha(TEXT_MUTED, 0.9),
            }}>
              {filled.length} item{filled.length !== 1 ? 's' : ''}
            </Typography>
            {lineTotal != null && (
              <Typography sx={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '0.72rem',
                fontWeight: 600,
                color: accent2,
              }}>
                {formatClaimLineItemAmount(lineTotal)}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {!summaryOnly && (
      <Box
        role="list"
        aria-label={summaryTitle}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          maxHeight,
          overflowY: 'auto',
          pr: 0.5,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { background: alpha(accent, 0.25), borderRadius: 4 },
        }}
      >
        {filled.map((row, idx) => {
          const parsedAmount = row.amount == null || row.amount === ''
            ? null
            : parseFloat(String(row.amount).replace(/[^0-9.]/g, ''));
          const amountLabel = Number.isNaN(parsedAmount) || parsedAmount == null
            ? null
            : formatClaimLineItemAmount(parsedAmount);
          return (
            <Box
              key={`${row.name}-${idx}`}
              role="listitem"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                px: 1.25,
                py: dense ? 0.6 : 0.7,
                borderRadius: '8px',
                background: alpha(SURFACE_TOP, 0.35),
                border: `1px solid ${alpha(accent, 0.08)}`,
                ml: 0.5,
                borderLeft: `2px solid ${alpha(accent, idx === 0 ? 0.35 : 0.12)}`,
              }}
            >
              <Typography sx={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '0.68rem',
                color: alpha(accent, 0.85),
                minWidth: 20,
                flexShrink: 0,
                pt: 0.1,
              }}>
                {idx + 1}.
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: dense ? '0.78rem' : '0.8rem',
                  color: TEXT_BODY,
                  lineHeight: 1.4,
                  wordBreak: 'break-word',
                }}>
                  {row.name}
                </Typography>
              </Box>
              {amountLabel && (
                <Typography sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '0.72rem',
                  color: accent2,
                  flexShrink: 0,
                }}>
                  {amountLabel}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
      )}
    </Box>
  );
};

export default ClaimLineItemsHierarchy;
